import { useQuery } from '@tanstack/react-query'

import { apiClient } from '../apiClient'

type PlatformViewProgramBreakdown = {
  program_id: string
  name: string
  display_name: string
  color: string
  icon_type?: string
  high_priority_engagement_count: number
  high_priority_within_target_count: number
  high_priority_missed_target_count: number
  high_priority_within_target_percentage: number
  engagement_count: number
  within_target_count: number
  missed_target_count: number
  within_target_percentage: number
}

type PlatformViewProgramVolumeResponse = {
  engagement_count: {
    total: number
    dailyAvg: number
    trend: number
  }
  within_target: {
    current: number
    trend: number
  }
  high_priority: {
    current: number
    total: number
    trend: number
  }
  summary: {
    program_id: string
    name: string
    display_name: string
    color: string
    missed_target_percentage: number
    missed_target_count: number
    within_target_count: number
    org_name: string
    org_profile_id: string
    logo_url: string
  }[]
  breakdown: {
    [date: string]: {
      engagement_count: number
      high_priority_engagement_count: number
      programs: PlatformViewProgramBreakdown[]
    }
  }
  volume_by_program: {
    program_id: string
    org_profile_id: string
    dailyAvg: number
    total: number
    trend: number
    within_target_trend: null
    high_priority_engagement_count: number
    high_priority_trend: number
  }[]
}

type PlatformViewProgramVolumeRequest = {
  start_time: string
  end_time: string
  granularity: 'daily' | 'monthly' | 'weekly'
}

const request = async ({
  granularity,
  ...params
}: PlatformViewProgramVolumeRequest): Promise<PlatformViewProgramVolumeResponse> => {
  const { data }: { data: PlatformViewProgramVolumeResponse } = await apiClient.get(
    `int/stats/platform/volume?granularity=${granularity}`,
    {
      params,
    }
  )

  return data
}

export function useGetVolume(params: Partial<PlatformViewProgramVolumeRequest>) {
  return useQuery<PlatformViewProgramVolumeResponse>({
    queryKey: ['platform-view-program-volume-breakdown', params],
    queryFn: () => request(params as PlatformViewProgramVolumeRequest),
    enabled: Boolean(params.start_time && params.end_time && params.granularity),
  })
}
